import FilterSelect, {type FilterSelectOption} from 'common/components/FilterSelect'
import FilePreviewModal, {
  canPreviewFile,
  findUploadedFile,
} from 'common/components/dataUpload/FilePreviewModal'
import {
  type DataUploadAssignmentQuestion,
  type DataUploadSheet,
  type DataUploadSheetAssignments,
  IGNORE_SHEET,
} from 'common/helpers/dataUpload'
import {Check, Eye, Table2} from 'lucide-react'
import React, {FC, useEffect, useId, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

interface Props {
  show: boolean
  /** The sheets to assign and the screens they may be assigned to, as the server sent them. */
  question: DataUploadAssignmentQuestion | null
  /** Busy while the files are being sent again with the answer. */
  submitting?: boolean
  onConfirm: (assignments: DataUploadSheetAssignments) => void
  /** The footer's Cancel, when there is no `onSkip`; the X too, when there is no `onClose`. */
  onCancel: () => void
  /**
   * The footer button becomes Skip: every sheet answered "Don't read it" (`IGNORE_SHEET`), which
   * the caller sends once.
   */
  onSkip?: (assignments: DataUploadSheetAssignments) => void
  /** The X (and Esc / backdrop): closes without answering; the question stays pending. */
  onClose?: () => void
  /** The uploaded files, so an image or PDF can be opened from its card to see what it is. */
  files?: readonly File[]
}

/** Sample rows shown per sheet: enough to recognise the list by, not a preview of the file. */
const MAX_SAMPLE_ROWS = 3

/** Column names listed as chips before the rest collapse into a "+N more" chip. */
const MAX_HEADER_CHIPS = 5

/** A sheet's own name, falling back to the key when the server sent no `sheet`. */
const sheetTitle = (sheet: DataUploadSheet) => sheet.sheet || sheet.key

/**
 * "Whose list is this?" — one card per sheet of a workbook that holds several lists of people and
 * says of none of them whose it is.
 *
 * A sheet headed `Teacher Name` or `Reg No` names its own screen and is never asked about; two
 * sheets both headed `First Name / Last Name` are, because only the school knows which is which.
 * Nothing was read when this is shown (so it costs no AI tokens and produces no wrong rows): the
 * same files are posted again with the answers, and a sheet may be left out with "Don't read it".
 *
 * Every sheet has to be answered — an answer that leaves one out has it asked about again, so
 * Continue stays disabled until none is left. The counter above the list says how many are still
 * open and an answered card takes a primary rail, so the remaining ones stand out in a workbook
 * with more sheets than fit on screen.
 */
const SheetAssignmentModal: FC<Props> = ({
  show,
  question,
  submitting = false,
  onConfirm,
  onCancel,
  onSkip,
  onClose,
  files = [],
}) => {
  const intl = useIntl()
  const fieldId = useId()
  const [assignments, setAssignments] = useState<DataUploadSheetAssignments>({})
  /** The file open in the preview dialog, over this one. */
  const [previewing, setPreviewing] = useState<{file: File; sheet?: string} | null>(null)
  /** The uploaded file a sheet belongs to; a workbook's sheets (Sheet1, Sheet3) all do. */
  const fileForSheet = (sheet: DataUploadSheet): File | undefined =>
    findUploadedFile(files, [sheet.fileName, sheet.sheet])
  // A new question (the next file) starts with nothing picked; closing still clears too.
  useEffect(() => {
    setAssignments({})
  }, [question])

  // `?? []` would be a new array each render, which every memo below would then depend on.
  const sheets = useMemo(() => question?.sheets ?? [], [question])
  const answeredCount = useMemo(
    () => sheets.filter((sheet) => assignments[sheet.key]).length,
    [sheets, assignments]
  )
  const answered = sheets.length > 0 && answeredCount === sheets.length

  /**
   * The server's screens, between the blank "Choose…" row the trigger shows until one is picked
   * (the grid's own placeholder convention) and "Don't read it" last: a sheet the school does
   * not want read — last year's roster on tab 3 — still has to be answered for, and that is the
   * answer. Picking the blank row again un-answers the sheet.
   */
  const options = useMemo<FilterSelectOption[]>(
    () => [
      {value: '', label: intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.CHOOSE'})},
      ...(question?.choices ?? []).map((choice) => ({value: choice.key, label: choice.title})),
      {value: IGNORE_SHEET, label: intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.IGNORE'})},
    ],
    [question, intl]
  )

  /** Several files in one upload: the file name is only worth showing when they differ. */
  const showFileNames = useMemo(
    () => new Set(sheets.map((sheet) => sheet.fileName ?? '')).size > 1,
    [sheets]
  )

  /**
   * Row count and column names as chips. The headers are what the user recognises the list by,
   * and a chip each keeps a ten-column sheet from becoming one unreadable run-on line.
   */
  const headerChips = (sheet: DataUploadSheet) => {
    const headers = sheet.headers ?? []
    const shown = headers.slice(0, MAX_HEADER_CHIPS)
    const rest = headers.length - shown.length
    return (
      <div className='d-flex flex-wrap gap-2 mt-2'>
        {sheet.rows != null && (
          <span className='sheet-assign-chip sheet-assign-chip-count'>
            {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.ROWS'}, {count: sheet.rows})}
          </span>
        )}
        {shown.map((header, index) => (
          <span key={`${sheet.key}-hc-${index}`} className='sheet-assign-chip' title={header}>
            {header}
          </span>
        ))}
        {rest > 0 && (
          <span className='sheet-assign-chip' title={headers.slice(MAX_HEADER_CHIPS).join(', ')}>
            {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.MORE_ROWS'}, {count: rest})}
          </span>
        )}
      </div>
    )
  }

  return (
    <>
      <Modal
        show={show}
        onHide={submitting ? undefined : (onClose ?? onCancel)}
        centered
        size='lg'
        backdrop={submitting ? 'static' : undefined}
        onExited={() => setAssignments({})}
      >
        <Modal.Header closeButton={!submitting} className='border-0 pb-0'>
          <div>
            <Modal.Title className='fs-3'>
              {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.TITLE'})}
            </Modal.Title>
            <p className='sheet-assign-hint mt-2 mb-0'>
              {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.HINT'})}
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className='pt-4'>
          {sheets.length > 1 && (
            <div
              className={`badge ${answered ? 'badge-light-primary' : 'badge-light'} fs-8 fw-semibold mb-3`}
              role='status'
            >
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.SHEETS.PROGRESS'},
                {answered: answeredCount, total: sheets.length}
              )}
            </div>
          )}

          {sheets.map((sheet) => {
            const chosen = assignments[sheet.key] ?? ''
            // Its file can be opened from the card (FilePreviewModal): a workbook, photo, PDF, …
            const file = fileForSheet(sheet)
            const preview = file && canPreviewFile(file) ? file : null
            return (
              <div key={sheet.key} className={`sheet-assign-card${chosen ? ' is-answered' : ''}`}>
                <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
                  <div className='flex-grow-1' style={{minWidth: 0}}>
                    <div className='d-flex align-items-center gap-2'>
                      {chosen ? (
                        <Check size={16} className='text-primary flex-shrink-0' aria-hidden />
                      ) : (
                        <Table2 size={16} className='text-muted flex-shrink-0' aria-hidden />
                      )}
                      <span className='sheet-assign-name'>{sheetTitle(sheet)}</span>
                      {preview && (
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary border border-primary ms-2 d-inline-flex align-items-center gap-1'
                          style={{padding: '0.25rem 0.75rem', fontSize: '0.8rem'}}
                          onClick={() => setPreviewing({file: preview, sheet: sheet.sheet})}
                        >
                          <Eye size={14} aria-hidden />
                          {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.PREVIEW'})}
                        </button>
                      )}
                    </div>
                    {/* A photo or PDF is its own list: its title already is the file name. */}
                    {showFileNames && sheet.fileName && sheet.fileName !== sheetTitle(sheet) && (
                      <div className='sheet-assign-file ms-6'>{sheet.fileName}</div>
                    )}
                    {headerChips(sheet)}
                  </div>

                  <div className='sheet-assign-field'>
                    <label className='sheet-assign-label' htmlFor={`${fieldId}-${sheet.key}`}>
                      {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.WHOSE_LIST'})}
                    </label>
                    <FilterSelect
                      id={`${fieldId}-${sheet.key}`}
                      value={chosen}
                      options={options}
                      onChange={(value) =>
                        setAssignments((prev) => ({...prev, [sheet.key]: value}))
                      }
                      disabled={submitting}
                      /*
                       * 'under', not the default fixed menu. This dialog opens from the onboarding
                       * wizard, whose steps sit in a transformed container, and `.modal` is itself
                       * a scroll container — a fixed menu is re-based by the transform and closes
                       * on the scroll the browser does to focus the trigger. An absolute menu
                       * moves with its card and inherits the dialog's stacking context, so it
                       * needs no z-index to out-rank the dialog. The dialog is deliberately not
                       * `scrollable`: that would make `.modal-body` clip the menu.
                       */
                      placement='under'
                      className='w-100'
                      triggerClassName='filter-select-solid w-100'
                      ariaLabel={intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.WHOSE_LIST'})}
                    />
                  </div>
                </div>

                {sheet.sample && sheet.sample.length > 0 && (
                  <div className='sheet-assign-sample'>
                    <div className='sheet-assign-sample-caption'>
                      {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.SAMPLE'})}
                    </div>
                    <div className='table-responsive'>
                      <table className='sheet-assign-table'>
                        {sheet.headers && sheet.headers.length > 0 && (
                          <thead>
                            <tr>
                              {sheet.headers.map((header, index) => (
                                <th key={`${sheet.key}-h-${index}`} title={header}>
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {sheet.sample.slice(0, MAX_SAMPLE_ROWS).map((row, rowIndex) => (
                            <tr key={`${sheet.key}-r-${rowIndex}`}>
                              {row.map((cell, cellIndex) => (
                                <td key={`${sheet.key}-r-${rowIndex}-c-${cellIndex}`} title={cell}>
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </Modal.Body>

        <Modal.Footer className='border-0 d-flex flex-wrap justify-content-between gap-3'>
          <span className='text-muted fs-8'>
            {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.NOT_REMEMBERED'})}
          </span>
          <div className='d-flex gap-2 ms-auto'>
            <button
              type='button'
              className='btn btn-light'
              disabled={submitting}
              onClick={
                onSkip
                  ? () =>
                      onSkip(Object.fromEntries(sheets.map((sheet) => [sheet.key, IGNORE_SHEET])))
                  : onCancel
              }
            >
              {intl.formatMessage({id: onSkip ? 'DATA_UPLOAD.COLUMNS.SKIP' : 'GENERAL.CANCEL'})}
            </button>
            <button
              type='button'
              className='btn btn-primary d-inline-flex align-items-center gap-2'
              disabled={!answered || submitting}
              onClick={() => onConfirm(assignments)}
            >
              {submitting && (
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                />
              )}
              {intl.formatMessage({
                id: submitting ? 'DATA_UPLOAD.SHEETS.READING' : 'DATA_UPLOAD.SHEETS.CONTINUE',
              })}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
      {/* Over this dialog: the file as it is, to tell which list it holds. */}
      <FilePreviewModal
        file={previewing?.file ?? null}
        sheet={previewing?.sheet}
        onClose={() => setPreviewing(null)}
      />
    </>
  )
}

export default SheetAssignmentModal
